import React, { useRef, useLayoutEffect, useEffect, useState } from 'react';
import 'mapbox-gl/dist/mapbox-gl.css';
import mapboxgl from 'mapbox-gl';

import './switcher/switcher.css';
import { SwitcherControl } from './switcher/switcher';
import { deviceCategories } from '../common/deviceCategories';
import { prepareIcon, loadImage } from './mapUtil';
import { styleCarto, styleMapbox, styleOsm } from './mapStyles';
import { t } from '../common/LocalizationProvider';
import { useAttributePreference } from '../common/preferences';
import palette from '../theme/palette';
import { registerMapAdapter, createMapboxAdapter } from '../mapControl';

const element = document.createElement('div');
element.style.width = '100%';
element.style.height = '100%';

export const map = new mapboxgl.Map({
    container: element,
    style: styleOsm(),
});

let ready = false;
const readyListeners = new Set();

const addReadyListener = listener => {
    readyListeners.add(listener);
    listener(ready);
};

const removeReadyListener = listener => {
    readyListeners.delete(listener);
};

const updateReadyValue = value => {
    ready = value;
    readyListeners.forEach(listener => listener(value));
};

const initMap = async () => {
    if (ready) return;
    const background = await loadImage('images/background.svg');
    map.addImage('background', await prepareIcon(background), {
        pixelRatio: window.devicePixelRatio,
    });
    await Promise.all(deviceCategories.map(async category => {
        for (const color of ['green', 'gray', 'red', 'yellow']) {
            const icon = await loadImage(`images/icon/${category}.svg`);
            map.addImage(`${category}-${color}`, prepareIcon(background, icon, palette.common[color]), {
                pixelRatio: window.devicePixelRatio,
            });
        }
    }));
    const pngEntries = deviceCategories.flatMap(category =>
        ['green', 'gray', 'red', 'yellow'].map(color => ({ category, color }))
    );
    await Promise.all(pngEntries.map(async ({ category, color }) => {
        try {
            const fileName = `${category}${color.charAt(0).toUpperCase() + color.slice(1)}.png`;
            const img = await loadImage(`/category/${fileName}`);
            const cpCanvas = document.createElement('canvas');
            const cpSize = 48;
            cpCanvas.width = cpSize * window.devicePixelRatio;
            cpCanvas.height = cpSize * window.devicePixelRatio;
            const cpCtx = cpCanvas.getContext('2d');
            const imgAspect = img.width / img.height;
            const canvasAspect = cpCanvas.width / cpCanvas.height;
            let drawW, drawH, drawX, drawY;
            if (imgAspect > canvasAspect) {
                drawW = cpCanvas.width;
                drawH = cpCanvas.width / imgAspect;
                drawX = 0;
                drawY = (cpCanvas.height - drawH) / 2;
            } else {
                drawH = cpCanvas.height;
                drawW = cpCanvas.height * imgAspect;
                drawX = (cpCanvas.width - drawW) / 2;
                drawY = 0;
            }
            cpCtx.drawImage(img, drawX, drawY, drawW, drawH);
            map.addImage(`${category}Position-${color}`, cpCtx.getImageData(0, 0, cpCanvas.width, cpCanvas.height), {
                pixelRatio: window.devicePixelRatio,
            });
        } catch (e) {
            console.warn(`[Tracthing] Skipped ${category}Position-${color}: ${e.message}`);
        }
    }));
    updateReadyValue(true);
};

map.on('load', initMap);

map.addControl(new mapboxgl.NavigationControl({
    showCompass: false,
}));

map.addControl(new SwitcherControl(
    [
        { title: t('mapOsm'), uri: styleOsm() },
        { title: t('mapCarto'), uri: styleCarto() },
        { title: t('mapMapboxStreets'), uri: styleMapbox('streets-v11') },
        { title: t('mapMapboxOutdoors'), uri: styleMapbox('outdoors-v11') },
        { title: t('mapMapboxSatellite'), uri: styleMapbox('satellite-v9') },
    ],
    t('mapOsm'),
    () => updateReadyValue(false),
    () => {
        const waiting = () => {
            if (!map.loaded()) {
                setTimeout(waiting, 100);
            } else {
                initMap();
            }
        };
        waiting();
    },
));

const Map = ({ children }) => {
    const containerEl = useRef(null);

    const [mapReady, setMapReady] = useState(false);

    const mapboxAccessToken = useAttributePreference('mapboxAccessToken');

    useEffect(() => {
        mapboxgl.accessToken = mapboxAccessToken;
    }, [mapboxAccessToken]);

    useEffect(() => {
        const listener = ready => setMapReady(ready);
        addReadyListener(listener);
        return () => {
            removeReadyListener(listener);
        };
    }, []);

    // Make this map the target for imperative actions (mapController) while
    // it is mounted; unregistering lets the Google host take over on its pages.
    useEffect(() => registerMapAdapter(createMapboxAdapter(map)), []);

    useLayoutEffect(() => {
        const currentEl = containerEl.current;
        currentEl.appendChild(element);
        if (map) {
            map.resize();
        }
        return () => {
            currentEl.removeChild(element);
        };
    }, [containerEl]);

    return (
        <div style={{ width: '100%', height: '100%' }} ref={containerEl}>
            {mapReady && children}
        </div>
    );
};

export default Map;